import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ScrollToTopButton from '../components/ScrollToTopButton';

// Add global wrapper to prevent horizontal overflow
const GlobalWrapper = styled.div`
  overflow-x: hidden;
  width: 100%;
  position: relative;
`;

// Container component for consistent layout
const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
  box-sizing: border-box;
`;

// Styled Components
const HeroSection = styled.section`
  height: 50vh;
  min-height: 300px;
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/images/background_taxi.jpg') no-repeat center center/cover;
  display: flex;
  align-items: center;
  text-align: center;
  color: white;
  width: 100%;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
  
  h1 {
    font-size: 3rem;
    margin-bottom: 20px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    margin-bottom: 30px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const Button = styled.a`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
  }
`;

const ServicesContainer = styled.section`
  padding: 50px 0;
  background-color: white;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 50px;
  width: 100%;
  
  h2 {
    font-size: 2.5rem;
    color: var(--primary-color);
    position: relative;
    display: inline-block;
    padding-bottom: 15px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
    
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background-color: var(--accent-color);
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ServiceCard = styled.div`
  background-color: var(--light-color);
  padding: 30px;
  border-radius: 10px;
  box-shadow: var(--shadow);
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  box-sizing: border-box;
  
  &:hover {
    transform: translateY(-10px);
  }
  
  h3 {
    color: var(--primary-color);
    margin-bottom: 15px;
    font-size: 1.5rem;
    word-wrap: break-word;
  }
  
  p {
    margin-bottom: 20px;
    word-wrap: break-word;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const BookingSection = styled.section`
  padding: 80px 0;
  background-color: var(--light-color);
  width: 100%;
  scroll-margin-top: 80px;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const BookingForm = styled.form`
  max-width: 800px;
  margin: 0 auto;
  padding: 30px;
  background-color: white;
  border-radius: 10px;
  box-shadow: var(--shadow);
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  padding: 0 15px;
  width: ${props => props.half ? '50%' : '100%'};
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    word-wrap: break-word;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
  
  textarea {
    height: 150px;
    resize: vertical;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  
  input {
    width: auto;
    margin-right: 10px;
    margin-top: 5px;
  }
  
  label {
    flex: 1;
    word-wrap: break-word;
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormInfo = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 30px;
  border-left: 4px solid var(--accent-color);
  box-sizing: border-box;
  width: 100%;
  
  p {
    margin: 0;
    word-wrap: break-word;
  }
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 20px;
  border-radius: 10px;
  margin: 0 15px 20px;
  text-align: center;
  box-shadow: var(--shadow);
  box-sizing: border-box;
  
  h3 {
    margin-bottom: 15px;
    color: #155724;
    word-wrap: break-word;
  }
  
  p {
    margin-bottom: 20px;
    word-wrap: break-word;
  }
  
  button {
    margin-top: 15px;
    background-color: var(--accent-color);
    color: var(--dark-color);
    padding: 12px 30px;
    border: none;
    border-radius: 5px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      background-color: #e0a800;
      transform: translateY(-3px);
    }
    
    @media (max-width: 768px) {
      width: 80%;
    }
  }
`;

function PersBefo() {
  const [selectedService, setSelectedService] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Check if URL has success parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setShowSuccess(true);
      // Scroll to booking form
      document.getElementById('buchung').scrollIntoView({behavior: 'smooth'});
    }
  }, []);
  
  // Handle service card clicks
  const handleServiceCardClick = (service) => {
    setSelectedService(service);
    
    // Scroll to booking form
    document.getElementById('buchung').scrollIntoView({behavior: 'smooth'});
  };
  
  const resetForm = () => {
    setShowSuccess(false);
    setSelectedService('');
  };
  
  // Define services
  const services = [
    { id: 'taxi', name: 'Taxi', description: 'Unser Taxiservice bringt Sie schnell und zuverlässig an Ihr Ziel. Professionelle Fahrer, moderne Fahrzeuge und faire Preise -- 24 Stunden am Tag, 7 Tage die Woche für Sie im Einsatz.' },
    { id: 'mietwagen', name: 'Mietwagen mit Chauffeur', description: 'Genießen Sie den Komfort eines Mietwagens mit persönlichem Chauffeur. Ideal für Geschäftstermine, Stadtbesichtigungen oder besondere Anlässe. Individueller Service nach Ihren Wünschen.' },
    { id: 'limousine', name: 'Limousinenservice', description: 'Stilvoller Transport in luxuriösen Fahrzeugen für besondere Anlässe wie Hochzeiten, Galas oder VIP-Transfers. Machen Sie Ihren besonderen Tag noch unvergesslicher mit unserem exklusiven Service.' },
    { id: 'shuttle', name: 'Shuttleservice', description: 'Zuverlässiger Transport für Gruppen bei Veranstaltungen, Konferenzen oder Betriebsfeiern. Wir organisieren den reibungslosen Transfer Ihrer Gäste oder Mitarbeiter nach individuellen Anforderungen.' },
    { id: 'flughafen', name: 'Flughafentransfer', description: 'Stressfreier Transfer zum und vom Flughafen. Wir überwachen Ihre Flugzeiten, helfen mit dem Gepäck und sorgen für eine pünktliche Ankunft -- damit Ihre Reise entspannt beginnt und endet.' },
    { id: 'dialyse', name: 'Dialysefahrtenservice', description: 'Regelmäßiger und zuverlässiger Transport zu Dialysebehandlungen. Unsere erfahrenen Fahrer sorgen für einen komfortablen und pünktlichen Transfer und berücksichtigen dabei Ihre individuellen Bedürfnisse.' },
    { id: 'reha', name: 'Reha- und Kurfahrtenservice', description: 'Zuverlässiger Transport zu Rehabilitationseinrichtungen und Kurorten. Wir passen uns Ihrem Behandlungsplan an und sorgen für eine stressfreie und komfortable Beförderung.' },
    { id: 'aufnahme', name: 'Krankenhausbehandlungsfahrten', description: 'Sicherer und komfortabler Transport zu geplanten Krankenhausaufenthalten. Wir helfen Ihnen mit dem Gepäck und sorgen für eine entspannte Anreise zur stationären Behandlung.' },
    { id: 'entlassung', name: 'Entlassungsfahrtenservice', description: 'Sorgfältiger Transport nach Hause nach einem Krankenhausaufenthalt. Unsere Fahrer holen Sie direkt von der Station ab und bringen Sie sicher und bequem nach Hause.' },
    { id: 'fernfahrten', name: 'Fernfahrtenservice', description: 'Längere Strecken bequem zurücklegen. Unser Fernfahrtenservice bietet Ihnen einen komfortablen Transport über größere Distanzen -- ideal für Geschäftsreisen oder private Fernreisen.' },
    { id: 'schuler', name: 'Schüler- und Mannschaftsfahrtenservice', description: 'Sicherer Transport für Schulklassen, Sportteams und Gruppen. Wir bieten zuverlässige Beförderung zu Schulveranstaltungen, Sportereignissen und Ausflügen mit erfahrenen Fahrern.' },
    { id: 'therapie', name: 'Strahlen- und Chemotherapieservice', description: 'Einfühlsamer Transport zu Strahlen- und Chemotherapie-Behandlungen. Unsere speziell geschulten Fahrer verstehen die besonderen Anforderungen während der Krebstherapie und sorgen für maximalen Komfort.' }
  ];
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    company: '',
    location: '',
    start_date: '',
    duration: '',
    requirements: '',
    privacy: false
  });
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  return (
    <GlobalWrapper>
      <HeroSection className="hero">
        <Container>
          <HeroContent className="hero-content">
            <h1>Personenbeförderung</h1>
            <p>Professionelle Beförderungsdienste für jede Anforderung - sicher, komfortabel und zuverlässig.</p>
            <Button href="#buchung" className="btn">Jetzt buchen</Button>
          </HeroContent>
        </Container>
      </HeroSection>

      <ServicesContainer className="services-container">
        <Container>
          <SectionTitle className="section-title">
            <h2>Unsere Beförderungsdienste</h2>
          </SectionTitle>
          
          <ServicesGrid className="services-grid">
            {services.map(service => (
              <ServiceCard 
                key={service.id}
                id={service.id}
                className="service-card"
                data-service={service.name}
                onClick={() => handleServiceCardClick(service.name)}
                role="button"
                tabIndex="0"
                aria-label={`Service auswählen: ${service.name}`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleServiceCardClick(service.name);
                  }
                }}
              >
                <h3>{service.name}</h3>
                <p>{service.description}</p>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </ServicesContainer>

      <BookingSection id="buchung" className="booking-section">
        <Container>
          <SectionTitle className="section-title">
            <h2>Online-Buchungsanfrage</h2>
          </SectionTitle>
          
          {showSuccess ? (
            <SuccessMessage>
              <h3>Vielen Dank für Ihre Buchungsanfrage!</h3>
              <p>Wir haben Ihre Anfrage erhalten und werden sie sorgfältig prüfen. Unser Team wird sich so schnell wie möglich mit Ihnen in Verbindung setzen, um die Details zu besprechen und Ihre Buchung zu bestätigen.</p>
              <button onClick={resetForm}>Neue Anfrage</button>
            </SuccessMessage>
          ) : (
            <BookingForm className="booking-form" id="booking-form" action="https://formsubmit.co/info@guardiansmoves.de" method="POST">
              <FormInfo>
                <p><strong>Hinweis:</strong> Bitte füllen Sie das Formular vollständig aus. Wir werden Ihre Anfrage prüfen und uns umgehend bei Ihnen melden, um die Buchung zu bestätigen.</p>
              </FormInfo>
              
              <input type="hidden" name="_subject" value="Neue Buchungsanfrage über die Website" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_next" value={window.location.origin + window.location.pathname + "?success=true"} />
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="Nachname" 
                    required 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="vorname">Vorname *</label>
                  <input 
                    type="text" 
                    id="vorname" 
                    name="Vorname" 
                    required 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="phone">Telefonnummer</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="Telefon" 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="email">E-Mail *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="E-Mail" 
                    required 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="service">Gewünschter Service *</label>
                  <select 
                    id="service" 
                    name="Service" 
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    required
                  >
                    <option value="">Bitte wählen</option>
                    {services.map(service => (
                      <option key={service.id} value={service.name}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="duration">Dauer/Zeitraum</label>
                  <input 
                    type="text" 
                    id="duration" 
                    name="Dauer" 
                    value={formData.duration}
                    onChange={handleChange}
                    placeholder="z.B. 1 Woche, 3 Monate, fortlaufend" 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="date">Datum *</label>
                  <input 
                    type="date" 
                    id="date" 
                    name="Datum" 
                    required 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="time">Uhrzeit *</label>
                  <input 
                    type="time" 
                    id="time" 
                    name="Uhrzeit" 
                    required 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="pickup">Abholadresse *</label>
                  <input 
                    type="text" 
                    id="pickup" 
                    name="Abholadresse" 
                    required 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="destination">Zieladresse *</label>
                  <input 
                    type="text" 
                    id="destination" 
                    name="Zieladresse" 
                    required 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="passengers">Anzahl der Personen *</label>
                  <input 
                    type="number" 
                    id="passengers" 
                    name="Personen_Anzahl" 
                    min="1" 
                    required 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="luggage">Gepäckstücke</label>
                  <input 
                    type="number" 
                    id="luggage" 
                    name="Gepäckstücke" 
                    min="0" 
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup className="form-group">
                <label htmlFor="special_requirements">Besondere Anforderungen oder Hinweise</label>
                <textarea 
                  id="special_requirements" 
                  name="Besondere_Anforderungen" 
                ></textarea>
              </FormGroup>
              
              <FormGroup className="form-group">
                <CheckboxGroup>
                  <input 
                    type="checkbox" 
                    id="privacy" 
                    name="Datenschutz_akzeptiert" 
                    required 
                  />
                  <label htmlFor="privacy">
                    Ich habe die <a href="/datenschutz/" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> gelesen und akzeptiere diese. *
                  </label>
                </CheckboxGroup>
              </FormGroup>
              
              <SubmitButton type="submit" className="btn">Buchungsanfrage senden</SubmitButton>
            </BookingForm>
          )}
        </Container>
      </BookingSection>
      
      <ScrollToTopButton />
    </GlobalWrapper>
  );
}

export default PersBefo;